/**
 * Custom image loader for Google Cloud Storage images
 * Handles both signed URLs (for private assets) and direct URLs (for public assets)
 *
 * For private GCS assets, use the AsyncImage component instead of Next.js Image directly
 *
 * Official docs: https://nextjs.org/docs/app/api-reference/next-config-js/images#loader-configuration
 */
const gcsImageLoader = ({ src, width, quality = 75 }) => {
  if (!src) return "";

  const appendSizeParams = (url) => {
    const separator = url.includes("?") ? "&" : "?";
    // Include width and optional quality so Next.js does not warn about missing width param
    return `${url}${separator}w=${encodeURIComponent(width || "auto")}${
      quality ? `&q=${encodeURIComponent(quality)}` : ""
    }`;
  };

  // 1) Absolute URLs (including signed URLs): leave as-is but append size params for compliance
  if (src.startsWith("http://") || src.startsWith("https://")) {
    return appendSizeParams(src);
  }

  // 2) Data URLs and blobs: leave exactly as-is (cannot append query params)
  if (src.startsWith("data:") || src.startsWith("blob:")) {
    return src;
  }

  // 3) Local public assets: keep them on the same origin
  //    Examples: "/logo.png", "/images/x.jpg", "/_next/static/media/xyz.png"
  //    Also normalize any mistakenly prefixed paths like "/public/xyz.png" → "/xyz.png"
  if (
    src.startsWith("/") ||
    src.startsWith("/_next/") ||
    src.startsWith("/static/") ||
    src.startsWith("/favicon") ||
    src.startsWith("/icons/") ||
    src.startsWith("/public/")
  ) {
    const normalized = src.startsWith("/public/")
      ? src.replace(/^\/public\//, "/")
      : src;
    return appendSizeParams(normalized);
  }

  // 4) Relative DB paths → construct with GCS base URL
  // Note: For private assets, use AsyncImage component instead of this loader
  const baseUrl = process.env.NEXT_PUBLIC_IMAGE_URL;
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const cleanSrc = src.startsWith("/") ? src.slice(1) : src;
  return appendSizeParams(`${cleanBaseUrl}${cleanSrc}`);
};

export default gcsImageLoader;
